import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { Product, ProductVariant, NewProductForm, VariantForm, PRODUCT_SELECT } from '../types/product';

/** Dati del miele, senza formati: si salvano con updateProduct. */
export type ProductUpdates = Partial<Omit<Product, 'id' | 'created_at' | 'product_variants'>>;

/** Quantità di un formato, modificate dalla tabella del catalogo. */
export type VariantStockUpdates = Partial<Pick<ProductVariant, 'stock' | 'inventory'>>;

// Campi del formato che si modificano dal form del prodotto. Le quantità
// (magazzino e online) no: si gestiscono solo dal catalogo, così un form
// aperto da tempo non può sovrascrivere una vendita avvenuta nel frattempo.
const variantDetails = (v: VariantForm) => ({
  label: v.label.trim(),
  grams: v.grams,
  price: v.price,
  original_price: v.original_price || null,
  discount_percent: v.discount_percent || null,
  sort_order: v.sort_order,
  active: v.active,
});

export const useProducts = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error } = await supabase
        .from('products')
        .select(PRODUCT_SELECT)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProducts(data || []);
    } catch (err: any) {
      setError('Impossibile caricare i prodotti: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const replaceProduct = (product: Product) =>
    setProducts((prev) => prev.map((p) => (p.id === product.id ? product : p)));

  const refetchProduct = async (productId: string) => {
    const { data, error } = await supabase.from('products').select(PRODUCT_SELECT).eq('id', productId).single();
    if (error) throw error;
    replaceProduct(data);
  };

  const addProduct = async (productData: NewProductForm) => {
    try {
      const { data: product, error } = await supabase
        .from('products')
        .insert([{
          name: productData.name,
          description: productData.description || null,
          long_description: productData.long_description || null,
          image_url: productData.image_url || null,
          category: productData.category || null,
        }])
        .select()
        .single();

      if (error) throw error;

      const { data: variants, error: variantsError } = await supabase
        .from('product_variants')
        .insert(productData.variants.map((v) => ({
          ...variantDetails(v),
          product_id: product.id,
          stock: v.stock,
          inventory: v.inventory,
        })))
        .select();

      if (variantsError) {
        // Niente prodotti senza formati: si annulla tutto.
        await supabase.from('products').delete().eq('id', product.id);
        throw variantsError;
      }

      setProducts((prev) => [{ ...product, product_variants: variants || [] }, ...prev]);
      return true;
    } catch (err: any) {
      setError('Impossibile aggiungere il prodotto: ' + err.message);
      return false;
    }
  };

  const updateProduct = async (productId: string, updates: ProductUpdates) => {
    try {
      const { error } = await supabase
        .from('products')
        .update(updates)
        .eq('id', productId);

      if (error) throw error;

      setProducts(prev =>
        prev.map(p => p.id === productId ? { ...p, ...updates } : p)
      );
      return true;
    } catch (err: any) {
      setError('Impossibile aggiornare il prodotto: ' + err.message);
      return false;
    }
  };

  /**
   * Allinea i formati del prodotto a quelli del form: aggiorna quelli
   * esistenti (senza toccare le quantità), crea i nuovi, elimina i tolti.
   */
  const saveVariants = async (productId: string, variants: VariantForm[]) => {
    try {
      const current = products.find((p) => p.id === productId)?.product_variants ?? [];
      const keptIds = new Set(variants.filter((v) => v.id).map((v) => v.id));
      const removed = current.filter((v) => !keptIds.has(v.id));

      if (removed.length > 0) {
        const { error } = await supabase
          .from('product_variants')
          .delete()
          .in('id', removed.map((v) => v.id));
        if (error) throw error;
      }

      for (const v of variants.filter((v) => v.id)) {
        const { error } = await supabase.from('product_variants').update(variantDetails(v)).eq('id', v.id!);
        if (error) throw error;
      }

      const added = variants.filter((v) => !v.id);
      if (added.length > 0) {
        const { error } = await supabase.from('product_variants').insert(
          added.map((v) => ({ ...variantDetails(v), product_id: productId, stock: v.stock, inventory: v.inventory }))
        );
        if (error) throw error;
      }

      await refetchProduct(productId);
      return true;
    } catch (err: any) {
      setError('Impossibile salvare i formati: ' + err.message);
      // Quel che è stato salvato prima dell'errore resta: ricarichiamo lo stato vero.
      await refetchProduct(productId).catch(() => {});
      return false;
    }
  };

  const updateVariantStock = async (productId: string, variantId: string, updates: VariantStockUpdates) => {
    try {
      const { error } = await supabase
        .from('product_variants')
        .update(updates)
        .eq('id', variantId);

      if (error) throw error;

      setProducts((prev) =>
        prev.map((p) =>
          p.id === productId
            ? { ...p, product_variants: p.product_variants.map((v) => (v.id === variantId ? { ...v, ...updates } : v)) }
            : p
        )
      );
      return true;
    } catch (err: any) {
      setError('Impossibile aggiornare la giacenza: ' + err.message);
      return false;
    }
  };

  const deleteProduct = async (productId: string) => {
    try {
      const { error } = await supabase
        .from('products')
        .delete()
        .match({ id: productId });

      if (error) throw error;

      setProducts(prev => prev.filter(p => p.id !== productId));
      return true;
    } catch (err: any) {
      setError('Impossibile eliminare il prodotto: ' + err.message);
      return false;
    }
  };

  return {
    products,
    loading,
    error,
    setError,
    fetchProducts,
    addProduct,
    updateProduct,
    saveVariants,
    updateVariantStock,
    deleteProduct
  };
};
